import { useState, useEffect } from 'react';
import firebase from './firebase'; // Import Firebase initialization

// Custom hooks
import { useAuth } from './hooks/useAuth';
import { useFirestoreData } from './hooks/useFirestoreData';
import { useNavigation } from './hooks/useNavigation';

// Layout components
import { LoadingScreen } from './components/layout/LoadingScreen';
import { AppHeader } from './components/layout/AppHeader';
import { Sidebar } from './components/layout/Sidebar';

// Common components
import { NotificationToast } from './components/common/NotificationToast';

// Views
import {
  LoginPage,
  DashboardView,
  TasksView,
  TemplatesView,
  UsersView,
  LocationsView
} from './views';

// Get the firebase instances for child components
export const auth = firebase.auth();
export const db = firebase.firestore();
export const functions = firebase.functions();

function App() {
  // Use custom hooks for state management
  const { currentUser, userProfile, loading, handleLogout } = useAuth();
  const { tasks, locations, users, loading: dataLoading } = useFirestoreData(currentUser);
  const { activeView } = useNavigation();

  // UI state
  const [language] = useState('de');
  const [notification, setNotification] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Reset menu states when viewport size changes
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser || !userProfile) {
    return <LoginPage language={language} />;
  }

  return (
    <div className="app-container">
      {/* Header */}
      <AppHeader
        userProfile={userProfile}
        currentUser={currentUser}
        onLogout={handleLogout}
        windowWidth={windowWidth}
      />

      {/* Sidebar */}
      <Sidebar
        activeView={activeView}
        userProfile={userProfile}
        windowWidth={windowWidth}
      />

      {/* Main Content */}
      <main className="main-content">
        {activeView === 'dashboard' && (
          <DashboardView
            currentUser={userProfile}
            language={language}
            tasks={tasks}
            locations={locations}
            users={users}
            loading={dataLoading}
          />
        )}
        {activeView === 'tasks' && (
          <TasksView
            currentUser={userProfile}
            language={language}
            tasks={tasks}
            locations={locations}
            users={users}
            loading={dataLoading}
          />
        )}
        {activeView === 'templates' && userProfile.role === 'admin' && (
          <TemplatesView currentUser={userProfile} language={language} />
        )}
        {activeView === 'locations' && userProfile.role === 'admin' && (
          <LocationsView language={language} />
        )}
        {activeView === 'users' && userProfile.role === 'admin' && (
          <UsersView language={language} />
        )}
      </main>

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

export default App;
